<script setup lang="ts">
import type { ProjectPersonType, ProjectSystemType } from '~/services-v2/financial-bff/__generated__/zeus'
import type { User } from '~/utils/auth/User'
import { cnpj, cnpjToApiPayload, cpf, cpfToApiPayload } from '@solfacil/frontend-utils'
// Services
import { SolButton, SolEmptyState, useToast } from '@solfacil/girassol'
import Loading from '~/components/loading/index.vue'
import { useAnalytics } from '~/composables/analytics/useAnalytics'
import { useTrackSimulationFinished } from '~/composables/analytics/useTrackSimulationFinished'
import { ENGINE_PRICING, FEATURE_FLAGS } from '~/constants'
import { ProjectCreateStatus } from '~/services-v2/financial-bff/__generated__/zeus'
import useAuth from '~/store-v2/auth/useAuth'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import usePartner from '~/store-v2/partners/usePartner'
import useFinancingTypes from '~/store-v2/projects/useFinancingTypes'
import useProjectCreate from '~/store-v2/projects/useProjectCreate'
import currencyUtils from '~/utils/currency'
import { getErrorMessage } from '~/utils/errors'
import { isKnownSystemType, isOnlyBattery } from '~/utils/project/systemType'

// feature flags
const { data: enableMultipleEnergyAccountsData } = useFeatureFlag<boolean>(FEATURE_FLAGS.ENABLE_MULTIPLE_ENERGY_ACCOUNTS, false, true)
const enableMultipleEnergyAccountsValue = computed(() => enableMultipleEnergyAccountsData.value || false)

const { data: newMotorData } = useFeatureFlag<boolean>(FEATURE_FLAGS.NEW_MOTOR, false, true)
const newMotorValue = computed(() => newMotorData.value || false)

const { t } = useI18n()
const router = useRouter()
const route = useRoute<any>()
const { trackEvent } = useAnalytics()
const { trackSimulationFinished } = useTrackSimulationFinished()
const shoppingCartId = ref<string | null>(null)

const { data: financingTypes, isLoading: isFinancingTypesLoading } = useFinancingTypes()

const {
  data: auth,
} = useAuth()

const partnerId = computed(() => {
  const id = auth.value?.person?.partner?.id
  return id !== undefined && id !== null ? String(id) : ''
})
const { data: partner, isLoading: isLoadingPartner } = usePartner(partnerId)

watch(partner, (newPartner) => {
  if (newPartner?.blocklist?.is_blocked) {
    router.push({ path: '/projects' })
  }
})

function goBack() {
  router.push('/projects')
}

onMounted(() => {
  shoppingCartId.value = String(route.query.shopping_cart_id) !== 'undefined' ? String(route.query.shopping_cart_id) : ''
  trackEvent('simulation_started')
})

const {
  createErrorToast,
} = useToast()

const optionsButtonReport = computed(() => {
  return [
    {
      title: t('simulation.newPage.pf.title'),
      description:
        t('simulation.newPage.pf.description'),
      financing: {
        title: t('simulation.newPage.pf.financing.title', { value: currencyUtils.formatBRL(Number(financingTypes?.value?.pf?.max_financing_value)) }),
        description: t('simulation.newPage.pf.financing.description', {
          maxFinancingInstallments: financingTypes?.value?.pf?.max_financing_installments,
          period: financingTypes?.value?.pf?.grace_period_financing?.label,
        }),
      },
      key: 'pf',
    },
    {
      title: t('simulation.newPage.pj.title'),
      description:
        t('simulation.newPage.pj.description'),
      financing: {
        title: t('simulation.newPage.pj.financing.title', { value: currencyUtils.formatBRL(Number(financingTypes?.value?.pj?.max_financing_value)) }),
        description: t('simulation.newPage.pj.financing.description', {
          maxFinancingInstallments: financingTypes?.value?.pj?.max_financing_installments,
          period: financingTypes?.value?.pj?.grace_period_financing?.label,
        }),
      },
      key: 'pj',
    },
  ]
})

onUpdated(() => {
  smoothScroll()
})

const downPayment = ref(0)
const downPaymentError = ref('')
const projectValue = ref(0)
const projectValueError = ref('')
const document = ref<string | undefined>()
const documentError = ref('')
const representativeDocument = ref<string | undefined>()
const representativeDocumentError = ref('')
/** Só mostra erro de obrigatoriedade depois de blur no campo ou tentativa de envio. */
const documentFieldTouched = ref(false)
const representativeDocumentFieldTouched = ref(false)
const systemPower = ref(0)
const systemPowerError = ref('')
const billValue = ref(0)
const billValueError = ref('')

// Only offer system types the front knows how to handle (a new backend value must not be selectable/sent).
const systemTypeOptions = computed(() => financingTypes.value?.system_types.filter(type => isKnownSystemType(type)).map(type => ({
  name: t(`simulation.newPage.form.system_type.${type}`),
  value: type,
})))

const systemType = ref<{ name: string, value: string }>()
const isOnlyBatterySystem = computed(() => isOnlyBattery(systemType.value?.value))

const agreeWithTerms = ref(false)
const agreeWithTermsError = ref('')

const maxFinancedValue = ref()
const minFinancedValue = ref()

const typeReport = ref<string | undefined>()
const typeReportError = ref('')
const typeReportOptions = ref<HTMLDivElement | undefined>()

const {
  mutateAsync: handleCreateProject,
  isPending: isCreatingProject,
} = useProjectCreate()

function downPaymentNotGtProjectValue(value: number, fromSubmit = false) {
  if (value >= projectValue.value && !fromSubmit)
    return 'Valor de entrada não pode ser maior que o valor do projeto'

  if (value < 0)
    return 'O valor de entrada não pode ser negativo'

  return ''
}

function projectError(value: number) {
  if (!value)
    return notEmpty(value)

  if (value < 0)
    return 'O valor do projeto não pode ser negativo'

  if ((projectValue.value - downPayment.value) > maxFinancedValue.value)
    return 'Valor do financiamento acima do permitido'

  if (minFinancedValue.value && value < minFinancedValue.value)
    return 'Valor do financiamento abaixo do permitido'

  return ''
}

function formatDocumentForApi(personType: string | undefined, value?: string) {
  if (!value)
    return null
  return personType === 'pf' ? cpfToApiPayload(value) : cnpjToApiPayload(value)
}

function formatRepresentativeDocumentForApi(value?: string) {
  if (!value)
    return null
  return cpfToApiPayload(value)
}

function notEmpty(value: number | string | null | undefined) {
  if (!value)
    return 'Campo obrigatório'

  return ''
}

function billValueRules(value: number) {
  let msg = ''
  if (!value || value === 0) {
    msg = notEmpty(value)
  }
  else if (typeof value === 'number') {
    if (value < 10)
      msg = 'O valor da conta de luz está abaixo do limite permitido'
    else if (value > 50000)
      msg = 'O valor da conta de luz excede o limite permitido'
  }
  return msg
}

function systemPowerRules(value: number) {
  const rangeError = isOnlyBatterySystem.value
    ? t('simulation.newPage.client_data.form.power.battery_capacity_range_error')
    : 'A potência do sistema deve estar entre 1 kWp e 999 kWp'

  if (!value || value === 0)
    return notEmpty(value)
  else if (typeof value === 'number' && (value < 1 || value > 999))
    return rangeError

  return ''
}

function invalidPowerMessage(onlyBattery: boolean) {
  return onlyBattery
    ? t('simulation.newPage.client_data.form.power.battery_capacity_error_422')
    : t('simulation.newPage.client_data.form.power.system_power_error_422')
}

const getDocument = computed(() => {
  return typeReport.value === 'pf' ? document.value : representativeDocument.value
})

function setErrorValue(message: string) {
  (typeReport.value === 'pf') ? documentError.value = message : representativeDocumentError.value = message
}

function markDocumentFieldTouched() {
  documentFieldTouched.value = true
}

function markRepresentativeDocumentFieldTouched() {
  representativeDocumentFieldTouched.value = true
}

function validateDocument() {
  const cpfValue = (getDocument.value ?? '').trim()
  if (!cpfValue) {
    setErrorValue(t('form.required'))
    return false
  }
  if (!cpf.validInput(cpfValue)) {
    setErrorValue(t('form.invalid_cpf'))
    return false
  }

  if (typeReport.value === 'pj') {
    const cnpjRaw = (document.value ?? '').trim()
    if (!cnpjRaw) {
      documentError.value = t('form.required')
      return false
    }
    if (!cnpj.validInput(document.value ?? '')) {
      documentError.value = t('form.invalid_cnpj')
      return false
    }
  }

  return true
}

watch(downPayment, (value) => {
  downPaymentError.value = downPaymentNotGtProjectValue(value)
  projectValueError.value = projectError(projectValue.value)
})

watch([document, typeReport, documentFieldTouched], ([value, type, docTouched]) => {
  const raw = value ?? ''

  if (type === 'pj') {
    if (!String(raw).trim()) {
      documentError.value = docTouched ? t('form.required') : ''
      return
    }
    const masked = cnpj.applyMask(String(raw))
    if (masked !== raw)
      document.value = masked
    const normalized = cnpj.normalize(masked)
    if (normalized.length === 14)
      documentError.value = cnpj.validInput(masked) ? '' : t('form.invalid_cnpj')
    else
      documentError.value = ''
    return
  }

  // PF (ou tipo ainda não escolhido): CPF no campo documento
  if (!String(raw).trim()) {
    documentError.value = docTouched ? t('form.required') : ''
    return
  }
  const cpfMasked = cpf.applyMask(String(raw))
  if (cpfMasked !== raw)
    document.value = cpfMasked
  const cpfNorm = cpf.normalize(cpfMasked)
  if (cpfNorm.length === 11)
    documentError.value = cpf.validInput(cpfMasked) ? '' : t('form.invalid_cpf')
  else
    documentError.value = ''
})
watch(billValue, (value) => {
  billValueError.value = billValueRules(value)
})

watch(systemPower, (value) => {
  systemPowerError.value = systemPowerRules(value)
})

// Power ↔ battery capacity: an error already on screen must follow the new unit (kWp ↔ kWh).
watch(isOnlyBatterySystem, (onlyBattery, wasOnlyBattery) => {
  if (!systemPowerError.value)
    return

  systemPowerError.value = systemPowerError.value === invalidPowerMessage(wasOnlyBattery)
    ? invalidPowerMessage(onlyBattery)
    : systemPowerRules(systemPower.value)
})

watch(projectValue, (value) => {
  projectValueError.value = projectError(value)
})

watch([representativeDocument, typeReport, representativeDocumentFieldTouched], ([value, type, repTouched]) => {
  if (type !== 'pj') {
    representativeDocumentError.value = ''
    return
  }
  const raw = value ?? ''
  if (!String(raw).trim()) {
    representativeDocumentError.value = repTouched ? t('form.required') : ''
    return
  }
  const masked = cpf.applyMask(String(raw))
  if (masked !== raw)
    representativeDocument.value = masked
  const normalized = cpf.normalize(masked)
  if (normalized.length === 11)
    representativeDocumentError.value = cpf.validInput(masked) ? '' : t('form.invalid_cpf')
  else
    representativeDocumentError.value = ''
})

const loadingMessages = t('loading.messages').split(', ')
const loadingInputFields = ref(false)

const user: User = useAppStorage().get('user')

const hasFieldErrors = computed(() => {
  return Boolean(typeReportError.value
    || downPaymentError.value
    || agreeWithTermsError.value
    || systemPowerError.value
    || projectValueError.value
    || billValueError.value
    || documentError.value
    || representativeDocumentError.value
    || !systemType.value,
  )
})

async function handleSubmit() {
  documentFieldTouched.value = true
  representativeDocumentFieldTouched.value = true

  projectValueError.value = projectError(projectValue.value)
  if (!document.value || !projectValue.value || !systemPower.value || !billValue.value) {
    billValueError.value = billValueRules(billValue.value)
    documentError.value = document.value?.toString().trim() ? '' : t('form.required')
    downPaymentError.value = downPayment.value < 0 ? downPaymentNotGtProjectValue(downPayment.value, true) : ''
    systemPowerError.value = systemPowerRules(systemPower.value)
    if (typeReport.value === 'pj')
      representativeDocumentError.value = representativeDocument.value?.toString().trim() ? '' : t('form.required')

    return
  }

  if (hasFieldErrors.value)
    return

  if (!validateDocument())
    return

  try {
    const response = await handleCreateProject({
      person_type: typeReport.value?.toUpperCase() as ProjectPersonType,
      document: formatDocumentForApi(typeReport.value, document.value) ?? '',
      representative_document: formatRepresentativeDocumentForApi(representativeDocument.value) ?? '',
      project_value: projectValue?.value,
      down_payment: downPayment?.value,
      bill_value: billValue.value,
      partner_id: user?.parceiro?.id,
      user_id: user?.id,
      system_power: systemPower.value,
      system_type: systemType.value?.value as ProjectSystemType,
      new_journey: true,
      provider: 'solfacil',
      engine_pricing: newMotorValue.value ? ENGINE_PRICING.NEW_ENGINE : ENGINE_PRICING.OLD_ENGINE,
      ...(shoppingCartId.value && {
        shopping_cart_id: shoppingCartId.value,
      }),
    })

    if (response.status === ProjectCreateStatus.SUCCESS) {
      trackSimulationFinished({
        result: response.status,
        success: true,
        projectId: response.id,
      })
      return router.push(`/simulation/result/${response.id}${shoppingCartId.value ? `?shopping_cart_id=${shoppingCartId.value}` : ''}`)
    }

    if (response.status === ProjectCreateStatus.CREATING) {
      trackSimulationFinished({
        result: response.status,
        success: true,
      })
      return router.push('/simulation/new/waiting')
    }
  }
  catch (e) {
    const code = getErrorMessage(e)
    trackSimulationFinished({
      result: 'error',
      success: false,
      reason: code,
    })

    if (code === 'INVALID_POWER') {
      systemPowerError.value = invalidPowerMessage(isOnlyBatterySystem.value)
      return
    }

    createErrorToast(t('simulation.newPage.client_data.form.error.title'))
  }
}

function selectedTypeReport(type: string) {
  setTypeReport(type)
  clearErrors()
}

async function setTypeReport(type: string) {
  try {
    loadingInputFields.value = true

    typeReport.value = type

    if (type) {
      const typeKey = `${type}`
      const maxFinancingValue = financingTypes?.value?.[typeKey]?.max_financing_value
      const minFinancingValue = financingTypes?.value?.[typeKey]?.min_financing_value
      maxFinancedValue.value = maxFinancingValue
      minFinancedValue.value = minFinancingValue
    }
    loadingInputFields.value = false
  }
  catch {
    loadingInputFields.value = false
  }
  finally {
    loadingInputFields.value = false
  }
}

function clearErrors() {
  const errors = [documentError, representativeDocumentError, projectValueError, downPaymentError, systemPowerError]
  errors.map(error => error.value = '')
  documentFieldTouched.value = false
  representativeDocumentFieldTouched.value = false
}

function smoothScroll() {
  if (typeReportOptions.value) {
    const topOffset = typeReportOptions.value.getBoundingClientRect().top + window.pageYOffset
    window.scrollTo({ top: topOffset, behavior: 'smooth' })
  }
}
const isAdmin = computed(() => {
  return !!(user?.perfil?.toLocaleLowerCase() === 'admin')
})

const isIntegradorLight = computed(() => {
  return !!(user?.perfil?.toLocaleLowerCase() === 'integrador_light')
})

const hasFinancingPermission = computed(() => auth.value?.person.partner?.permission.financing === true)
</script>

<template>
  <Loading v-if="isLoadingPartner" />
  <div v-else class="initial-page-container">
    <NavigationHeaderBar stage="simulation" />
    <div v-if="hasFinancingPermission">
      <div class="simulation-card">
        <form class="simulation-form w-full" @submit.prevent="handleSubmit">
          <div v-if="isCreatingProject" class="loading-container w-full flex justify-center">
            <Loading :messages="loadingMessages" />
          </div>
          <div
            v-if="!isCreatingProject && !isAdmin && !isIntegradorLight"
            class="simulation-content flex flex-col justify-between w-full"
          >
            <span v-if="financingTypes" class="fonts-body-large-bold font-bold">
              <h1 class="text-sm font-bold">{{ t('simulation.newPage.title') }}</h1>
              <span class="text-neutral-low-light text-2xs font-regular">{{ t('simulation.newPage.description')
              }}</span>
            </span>
            <div class="flex flex-col justify-between w-full">
              <span class="text-stroke-feedback-negative-dark">{{ typeReportError }}</span>

              <SimulationMultiSelectButton
                v-if="financingTypes && !isFinancingTypesLoading"
                :options="optionsButtonReport" :active="typeReport" @selected="selectedTypeReport"
              />

              <div v-if="isFinancingTypesLoading" class="flex w-full items-center justify-center">
                <Loading />
              </div>

              <span v-if="typeReportError" class="error-message">{{ typeReportError }}</span>
            </div>

            <div v-if="!financingTypes && !isFinancingTypesLoading" class="w-full items-center justify-center">
              <SolEmptyState
                id="empty-financing-types" class="max-w-full" :title="t('simulation.newPage.empty.title')"
                :subtitle="t('simulation.newPage.empty.subtitle')" variant="no-content"
              />
            </div>
            <div v-if="loadingInputFields && typeReport" class="w-full h-40">
              <div class="loader-lines" />
            </div>
            <div v-if="typeReport && !loadingInputFields" ref="typeReportOptions" class="pt-xs">
              <span class="title">
                {{ t('simulation.newPage.client_data.title') }}
              </span>
              <p class="sub-title">
                {{ t('simulation.newPage.client_data.description') }}
              </p>

              <div class="input-options grid-cols-1 md:system:grid-cols-3">
                <SolInputText
                  v-if="typeReport === 'pf' || !typeReport"
                  id="document-pf"
                  v-model="document"
                  data-testid="document-pf-input"
                  :error="documentError"
                  :disabled="!typeReport"
                  name="document"
                  :mask="cpf.mask"
                  :label="t('simulation.newPage.client_data.form.document_pf.title')"
                  placeholder="000.000.000-00"
                  @blur="markDocumentFieldTouched"
                />
                <SolInputText
                  v-if="typeReport === 'pj'"
                  id="document-pj"
                  v-model="document"
                  data-testid="document-pj-input"
                  :error="documentError"
                  :disabled="!typeReport"
                  name="document"
                  :mask="cnpj.mask"
                  :label="t('simulation.newPage.client_data.form.document_pj.title')"
                  placeholder="00.000.000/0000-02"
                  @blur="markDocumentFieldTouched"
                />
                <SolInputText
                  v-if="typeReport === 'pj'"
                  id="representative-document"
                  v-model="representativeDocument"
                  data-testid="representative-document-input"
                  :error="representativeDocumentError"
                  :disabled="!typeReport"
                  name="representativeDocument"
                  :mask="cpf.mask"
                  :label="t('simulation.newPage.client_data.form.document_pj.majoritary.title')"
                  placeholder="000.000.000-00"
                  @blur="markRepresentativeDocumentFieldTouched"
                />
                <InputMoneyInput
                  id="project-value"
                  v-model="projectValue"
                  data-testid="project-value-input"
                  :disabled="!typeReport"
                  size="medium"
                  required
                  :error="projectValueError"
                  name="projectValue"
                  :label="t('simulation.newPage.client_data.form.value.title')"
                  :placeholder="t('simulation.newPage.client_data.form.value.placeholder')"
                />
                <InputMoneyInput
                  id="down-payment"
                  v-model="downPayment"
                  data-testid="down-payment-input"
                  :disabled="!typeReport"
                  size="medium"
                  name="downPayment"
                  :error="downPaymentError"
                  :label="t('simulation.newPage.client_data.form.down_payment.title')"
                  :placeholder="t('simulation.newPage.client_data.form.down_payment.placeholder')"
                />
                <SolSelect
                  id="system-type"
                  v-model:selected="systemType"
                  class="h-max"
                  data-testid="system-type-select"
                  :options="systemTypeOptions"
                  :disabled="!typeReport"
                  name="systemType"
                  :label="t('simulation.newPage.form.system_type.label')"
                  :placeholder="t('simulation.newPage.form.system_type.placeholder')"
                  :required="true"
                />
                <div>
                  <InputMoneyInput
                    id="bill-value"
                    v-model="billValue"
                    data-testid="bill-value-input"
                    :disabled="!typeReport"
                    size="medium"
                    name="billValue"
                    :error="billValueError"
                    :label="enableMultipleEnergyAccountsValue ? t('simulation.newPage.client_data.form.bill_value.title') : t('simulation.newPage.client_data.form.bill_value.old_title')"
                    :placeholder="t('simulation.newPage.client_data.form.bill_value.placeholder')"
                    :helper-text="enableMultipleEnergyAccountsValue ? t('simulation.newPage.client_data.form.bill_value.helperText') : t('simulation.newPage.client_data.form.bill_value.old_helper_text')"
                  />
                  <SharedHelperElectricityBill v-if="enableMultipleEnergyAccountsValue" />
                </div>
                <InputSystemPower
                  id="system-power"
                  v-model="systemPower"
                  data-testid="system-power-input"
                  :disabled="!typeReport"
                  size="medium"
                  name="systemPower"
                  :error="systemPowerError"
                  :label="isOnlyBatterySystem ? t('simulation.newPage.client_data.form.power.battery_capacity_title') : t('simulation.newPage.client_data.form.power.title')"
                  :placeholder="isOnlyBatterySystem ? t('simulation.newPage.client_data.form.power.battery_capacity_placeholder') : t('simulation.newPage.client_data.form.power.placeholder')"
                  :unit="isOnlyBatterySystem ? 'kWh' : 'kWp'"
                />
              </div>
              <SolCheckbox
                id="is-admin" name="admin" value="yes" :checked="agreeWithTerms"
                @change="agreeWithTerms = !agreeWithTerms"
              >
                <span>{{ t('simulation.cpf_use_terms.client') }}</span>
                <a
                  href="https://helpcentersolfacil.zendesk.com/hc/pt-br/articles/26540116637211-TERMOS-E-CONDI%C3%87%C3%95ES-PLATAFORMA-SOLF%C3%81CIL-TERMO"
                  target="_blank" class="ml-1 text-brand-secondary-pure hover:text-brand-secondary-dark"
                >
                  {{ t('simulation.cpf_use_terms.terms') }}
                </a>
                <span>{{ t('simulation.cpf_use_terms.with') }}</span>
                <a
                  href="https://landing.solfacil.com.br/scr/" target="_blank"
                  class="mx-1 text-brand-secondary-pure hover:text-brand-secondary-dark"
                >
                  {{ t('simulation.cpf_use_terms.terms_authorization') }}
                </a>
                <span>{{ t('simulation.cpf_use_terms.and_with') }}</span>
                <a
                  href="https://landing.solfacil.com.br/politica-de-privacidade/?_gl=1*a56jbv*_ga*MTg5MDEwMTU0OS4xNjk3MDc2Mzkx*_ga_NQJ9ZNTL71*MTY5NzIwNzM2NC4zLjEuMTY5NzIwNzM3My41MS4wLjA./"
                  target="_blank" class="mx-1 text-brand-secondary-pure hover:text-brand-secondary-dark"
                >
                  {{ t('simulation.cpf_use_terms.privacy_policy') }}
                </a>
              </SolCheckbox>
              <span class="error-message">{{ agreeWithTermsError }}</span>
            </div>
          </div>

          <div v-if="!isAdmin && !isIntegradorLight && !isCreatingProject" class="submit-container">
            <SolButton
              id="analyze_credit"
              class="self-end"
              type="submit"
              :disabled="!agreeWithTerms || isCreatingProject || isFinancingTypesLoading || hasFieldErrors"
              size="large"
            >
              {{ t('app.create_new_simulation') }}
            </SolButton>
          </div>
        </form>
      </div>
    </div>

    <div v-else>
      <div class="simulation-card">
        <div class="my-4 card-container flex flex-col justify-between w-full items-center">
          <SolEmptyState
            id="empty-adm-solfacil" title="Seu perfil é básico e não permite acesso a essa função"
            subtitle="Devido à política de precificação, você ainda não tem acesso às linhas de financiamento. Entre em contato com o atendimento para verificar os requisitos para acessar o financiamento."
            variant="no-content"
          >
            <template #action>
              <SolButton
                id="go" class="self-end" type="submit" size="large" @click="goBack"
              >
                {{ t('app.back') }}
              </SolButton>
            </template>
          </SolEmptyState>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.initial-page-container {
  @apply flex flex-col py-2xs px-4xs gap-4 w-full;

  @screen md:system {
    @apply pb-sm px-2xs;
  }

  .simulation-card {
    @apply bg-neutral-high-pure border border-neutral-high-medium rounded-2xl;
    @apply p-2xs flex flex-col gap-4 w-full ;
    box-sizing: border-box;

    .simulation-form {
      @apply flex flex-col gap-5 w-full;

      .loader-lines {
        @apply h-40 rounded-lg mt-5;
        @apply top-0 right-0 bottom-0 left-0 z-50;
        background: rgba(#fff, 0.9) url(https://cdn.solfacil.com.br/assets/img/loading-for-legacy.gif) no-repeat center center;
      }

      .loading-container {
        @apply my-4;
      }

      .simulation-content {
        @apply flex flex-col justify-between w-full;

        .title {
          @apply flex justify-between;
          @apply fonts-body-large-bold;
          @apply text-neutral-low-dark text-sm;
          @apply pb-1;
        }

        .sub-title {
          @apply fonts-subtitle-small text-neutral-low-light text-2xs;
        }
      }
    }

    .submit-container {
      @apply border-t-neutral-low-light w-full flex justify-end;

      @screen xsm:system {
        @apply justify-center;
      }

      >button {
        @screen xsm:system {
          @apply w-full;
        }
      }
    }

    .input-options {
      @apply my-2xs grid gap-4;
    }

    .error-message {
      @apply text-feedback-negative-pure py-nano;
      @apply fonts-subtitle-small;
    }
  }
}
</style>
